/**
 * 学生番号入力モーダル
 */
export class StudentLoginModal {
  private modal: HTMLDivElement
  private overlay: HTMLDivElement
  private studentIdInput: HTMLInputElement
  private submitButton: HTMLButtonElement
  private errorMessage: HTMLDivElement
  private onSubmit: (studentId: string) => void

  constructor(onSubmit: (studentId: string) => void) {
    this.onSubmit = onSubmit
    this.createModal()
    this.attachEventListeners()
  }

  private createModal(): void {
    // オーバーレイ
    this.overlay = document.createElement('div')
    this.overlay.className = 'student-login-overlay'
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `

    // モーダル本体
    this.modal = document.createElement('div')
    this.modal.className = 'student-login-modal'
    this.modal.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 32px;
      width: 400px;
      max-width: 90%;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      animation: fadeIn 0.3s ease-out;
    `

    // タイトル
    const title = document.createElement('h2')
    title.textContent = '学生番号を入力してください'
    title.style.cssText = `
      margin: 0 0 24px 0;
      font-size: 20px;
      color: #333;
      text-align: center;
    `

    // 説明文
    const description = document.createElement('p')
    description.textContent = 'この学習ツールの使用状況を記録するため、学生番号が必要です。'
    description.style.cssText = `
      margin: 0 0 24px 0;
      font-size: 14px;
      color: #666;
      text-align: center;
      line-height: 1.5;
    `

    // 入力フィールドコンテナ
    const inputContainer = document.createElement('div')
    inputContainer.style.cssText = `
      margin-bottom: 16px;
    `

    // ラベル
    const label = document.createElement('label')
    label.textContent = '学生番号'
    label.style.cssText = `
      display: block;
      margin-bottom: 8px;
      font-size: 14px;
      color: #333;
      font-weight: 500;
    `

    // 入力フィールド
    this.studentIdInput = document.createElement('input')
    this.studentIdInput.type = 'text'
    this.studentIdInput.placeholder = '例: 20240001'
    this.studentIdInput.style.cssText = `
      width: 100%;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 16px;
      transition: border-color 0.2s;
      box-sizing: border-box;
    `

    // エラーメッセージ
    this.errorMessage = document.createElement('div')
    this.errorMessage.style.cssText = `
      color: #d32f2f;
      font-size: 12px;
      margin-top: 8px;
      display: none;
    `

    // 送信ボタン
    this.submitButton = document.createElement('button')
    this.submitButton.textContent = 'ログイン'
    this.submitButton.style.cssText = `
      width: 100%;
      padding: 12px;
      background-color: #1976d2;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.2s;
      margin-top: 16px;
    `

    // 要素の組み立て
    inputContainer.appendChild(label)
    inputContainer.appendChild(this.studentIdInput)
    inputContainer.appendChild(this.errorMessage)

    this.modal.appendChild(title)
    this.modal.appendChild(description)
    this.modal.appendChild(inputContainer)
    this.modal.appendChild(this.submitButton)

    this.overlay.appendChild(this.modal)
  }

  private attachEventListeners(): void {
    // 入力フィールドのフォーカス時
    this.studentIdInput.addEventListener('focus', () => {
      this.studentIdInput.style.borderColor = '#1976d2'
      this.hideError()
    })

    // 入力フィールドのブラー時
    this.studentIdInput.addEventListener('blur', () => {
      this.studentIdInput.style.borderColor = '#ddd'
    })

    // Enterキーでの送信
    this.studentIdInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.handleSubmit()
      }
    })

    // ボタンクリック
    this.submitButton.addEventListener('click', () => {
      this.handleSubmit()
    })

    // ホバー効果
    this.submitButton.addEventListener('mouseenter', () => {
      this.submitButton.style.backgroundColor = '#1565c0'
    })

    this.submitButton.addEventListener('mouseleave', () => {
      this.submitButton.style.backgroundColor = '#1976d2'
    })
  }

  private handleSubmit(): void {
    const studentId = this.studentIdInput.value.trim()

    // バリデーション
    if (!studentId) {
      this.showError('学生番号を入力してください')
      return
    }

    if (!/^\d{6,10}$/.test(studentId)) {
      this.showError('学生番号は6〜10桁の数字で入力してください')
      return
    }

    // LocalStorageに保存
    localStorage.setItem('studentId', studentId)

    // コールバック実行
    this.onSubmit(studentId)

    // モーダルを閉じる
    this.close()
  }

  private showError(message: string): void {
    this.errorMessage.textContent = message
    this.errorMessage.style.display = 'block'
    this.studentIdInput.style.borderColor = '#d32f2f'
  }

  private hideError(): void {
    this.errorMessage.style.display = 'none'
  }

  public show(): void {
    document.body.appendChild(this.overlay)
    
    // アニメーション用のCSS追加
    const style = document.createElement('style')
    style.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `
    document.head.appendChild(style)

    // 入力フィールドにフォーカス
    setTimeout(() => {
      this.studentIdInput.focus()
    }, 100)
  }

  public close(): void {
    this.overlay.remove()
  }

  /**
   * 学生番号がすでに保存されているかチェック
   */
  public static getStoredStudentId(): string | null {
    return localStorage.getItem('studentId')
  }

  /**
   * 保存された学生番号をクリア
   */
  public static clearStoredStudentId(): void {
    localStorage.removeItem('studentId')
  }
}